import React, { useMemo } from 'react';

import { DEFAULT_MAX_PROGRESS_VALUE, PERCENTAGE_100 } from '@/ui/ProgressBar/constants';
import { POSITIONS_TO_DEG } from './constants';
import { RootStyled, CircleContainerStyled, CircleTrackStyled, CircleProgressStyled, InnerContent, FakeCorner } from './styles';
import { CircularProgressBarData, CircularProgressBarProps } from './types';

const getCircleSetup = ({
  value,
  maxValue = DEFAULT_MAX_PROGRESS_VALUE,
  size,
  strokeWidth,
  gap = 0,
  startPosition = 'top',
}: CircularProgressBarData): {
  center: number;
  radius: number;
  rotateDeg: number;
  arcLength: number;
  arcOffset: number;
  innerContentSize: number;
  overflowed?: boolean;
} => {
  const overflowed = value > (maxValue || DEFAULT_MAX_PROGRESS_VALUE);

  const progress = Math.min(Math.round((value / maxValue) * PERCENTAGE_100), PERCENTAGE_100);

  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const arcLength = 2 * Math.PI * radius;

  const gapToArcLength = (overflowed ? 0 : gap) / arcLength;

  const arcGapMultiplier = 1 - gapToArcLength;
  const arcOffset = arcLength * ((100 - progress * arcGapMultiplier) / 100);

  const gapDeg = 360 * gapToArcLength;
  const gapCenter = gapDeg / 2;
  const rotateDeg = POSITIONS_TO_DEG[startPosition] + gapCenter;

  const innerContentSize = size - strokeWidth * 2;
  return {
    overflowed,
    center,
    radius,
    rotateDeg,
    arcLength,
    arcOffset,
    innerContentSize,
  };
};

export const CircularProgressBar = ({
  value,
  maxValue,
  size,
  strokeWidth,
  gap = 0,
  startPosition,
  rounded,
  children,
  customTrackBgColor,
}: CircularProgressBarProps): JSX.Element => {
  const { center, radius, rotateDeg, arcLength, arcOffset, innerContentSize, overflowed } = useMemo(
    () => getCircleSetup({ value, maxValue, size, strokeWidth, gap, startPosition }),
    [value, maxValue, size, strokeWidth, gap, startPosition],
  );

  const commonCircleProps = {
    center,
    radius,
    strokeWidth,
    rounded,
  };

  return (
    <RootStyled>
      <CircleContainerStyled size={size} rotateDeg={rotateDeg} overflowed={overflowed}>
        <CircleTrackStyled
          {...commonCircleProps}
          arcLenght={overflowed ? arcLength : arcLength - gap}
          customTrackBgColor={customTrackBgColor}
        />
        <CircleProgressStyled {...commonCircleProps} arcLenght={arcLength} arcOffset={arcOffset} />
      </CircleContainerStyled>
      {children && <InnerContent size={innerContentSize}>{children}</InnerContent>}
      <FakeCorner visible={overflowed} strokeWidth={strokeWidth} />
    </RootStyled>
  );
};
